import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ArrowRight, Sparkles, Heart, Gift } from "lucide-react";
import { HomepageSection as HomepageSectionType } from "@/hooks/useHomepageSections";
import { Json } from "@/integrations/supabase/types";
import { useLanguage } from "@/hooks/useLanguage";
import { AnayaConceptDescription } from "@/components/AnayaConceptDescription";
import { conceptGiftDestination, conceptText } from "@/lib/anayaConcept";

// Default images for known sections
import bottleDesert from "@/assets/bottle-desert.jpg";
import signatureBox from "@/assets/signature-box.jpg";
import sensationLady from "@/assets/sensation-lady.jpg";
import corporateRamadanBox from "@/assets/corporate-ramadan-box.png";
import specialOccasionsWeddingBox from "@/assets/special-occasions-wedding-box.png";

interface HomepageSectionProps {
  section: HomepageSectionType;
  formatWhatsAppLink: () => string;
}

// Helper to get content value from section
const getContent = (content: Json | null, key: string): string => {
  if (content && typeof content === 'object' && !Array.isArray(content)) {
    return String((content as Record<string, unknown>)[key] || '');
  }
  return '';
};

// Default images by section key
const defaultImages: Record<string, string> = {
  for_every_moment: bottleDesert,
  signature_box: signatureBox,
  corporate_gifting: corporateRamadanBox,
  special_occasions: specialOccasionsWeddingBox,
  final_expression: sensationLady,
};

const HomepageSectionComponent = ({ section, formatWhatsAppLink }: HomepageSectionProps) => {
  const { section_key, section_title, section_title_ar, subtitle, subtitle_ar, description, description_ar, content, content_ar } = section;
  const { getLocalizedField, t, isRTL } = useLanguage();

  const localizedTitle = getLocalizedField(section_title, section_title_ar);
  const localizedSubtitle = getLocalizedField(subtitle, subtitle_ar);
  const localizedDescription = getLocalizedField(description, description_ar);

  // Helper to get content value - tries Arabic content first when in Arabic mode
  const getContentLocalized = (key: string): string => {
    const arContent = content_ar && typeof content_ar === 'object' && !Array.isArray(content_ar)
      ? String((content_ar as Record<string, unknown>)[key] || '') : '';
    const enContent = getContent(content, key);
    return getLocalizedField(enContent, arContent || undefined);
  };

  const imageUrl = getContentLocalized('image_url') || defaultImages[section_key] || '';
  const ctaText = getContentLocalized('cta_text');
  // Links/URLs always come from the canonical (English) content record so a
  // translated string can never replace a URL or layout key.
  const ctaLink = getContent(content, 'cta_link');
  const learnMoreLink = getContent(content, 'learn_more_link');
  const description2 = getContentLocalized('description2');
  const layoutType = getContent(content, 'layout_type') || 'default';

  const arrowClass = `h-4 w-4${isRTL ? " rotate-180" : ""}`;

  // How It Works section - special 3-column layout
  if (section_key === 'how_it_works') {
    const steps = [
      {
        icon: <Sparkles className="h-8 w-8" />,
        title: getContentLocalized('step1_title') || t('home.step1Title'),
        description: getContentLocalized('step1_description') || t('home.step1Description'),
      },
      {
        icon: <Heart className="h-8 w-8" />,
        title: getContentLocalized('step2_title') || t('home.step2Title'),
        description: getContentLocalized('step2_description') || t('home.step2Description'),
      },
      {
        icon: <Gift className="h-8 w-8" />,
        title: getContentLocalized('step3_title') || t('home.step3Title'),
        description: getContentLocalized('step3_description') || t('home.step3Description'),
      },
    ];

    return (
      <section className="py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-4">
              <span className="text-gold-dark text-base uppercase tracking-[0.3em] mb-4 block">
                {localizedSubtitle || t('home.theExperience')}
              </span>
              <h2 className="font-display text-4xl md:text-5xl text-foreground">
                {localizedTitle || t('nav.howItWorks')}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <Link
                key={index}
                to="/quiz"
                onClick={() => sessionStorage.setItem("startFreshQuiz", "true")}
                className="text-center p-8 rounded-2xl bg-strip-beige border border-gold/20 hover:border-gold/50 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group shadow-sm cursor-pointer block"
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gold/10 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-primary-foreground transition-all duration-300">
                  {step.icon}
                </div>
                <h3 className="font-display text-xl text-foreground mb-3">{step.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{step.description}</p>
              </Link>
            ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // For Every Moment - Image Right, Text Left
  if (section_key === 'for_every_moment' || layoutType === 'image_right') {
    return (
      <section className="py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <span className="text-gold-dark text-base uppercase tracking-[0.3em] mb-1 block">
                {localizedSubtitle || t('home.threeFragrances')}
              </span>
              <h2 className="font-display text-4xl md:text-5xl text-foreground mb-6">
                {localizedTitle || t('home.forEveryMoment')}
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                {localizedDescription || t('home.forEveryMomentDescription')}
              </p>
            </div>
            <div className="flex flex-col items-center">
              {imageUrl && (
                <div className="w-full">
                  <AspectRatio ratio={3 / 2} className="relative overflow-hidden rounded-2xl bg-muted">
                    <img
                      src={imageUrl}
                      alt={localizedTitle}
                      className="h-full w-full object-cover shadow-elevated brightness-110 saturate-125 contrast-105"
                      loading="lazy"
                    />
                    <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gold/20 rounded-full blur-3xl" />
                  </AspectRatio>
                </div>
              )}
              {ctaLink && (
                <Link to={ctaLink} className="mt-6" onClick={() => ctaLink === "/quiz" && sessionStorage.setItem("startFreshQuiz", "true")}>
                  <Button variant="gold" size="lg" className="gap-2">
                    {ctaText || t('btn.learnMore')}
                    <ArrowRight className={arrowClass} />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Signature Box - Image Left, Text Right
  if (section_key === 'signature_box' || layoutType === 'image_left') {
    return (
      <section className="py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="order-2 lg:order-1 flex flex-col items-center">
              {imageUrl && (
                <div className="w-full">
                  <AspectRatio ratio={3 / 2} className="relative overflow-hidden rounded-2xl bg-muted">
                    <img
                      src={imageUrl}
                      alt={localizedTitle}
                      className="h-full w-full object-cover shadow-elevated brightness-110 saturate-125 contrast-105"
                      loading="lazy"
                    />
                    <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gold/20 rounded-full blur-3xl" />
                  </AspectRatio>
                </div>
              )}
              {ctaLink && (
                <Link to={ctaLink} className="mt-6" onClick={() => ctaLink === "/quiz" && sessionStorage.setItem("startFreshQuiz", "true")}>
                  <Button variant="gold" size="lg" className="gap-2">
                    {ctaText || t('home.explore')}
                    <ArrowRight className={arrowClass} />
                  </Button>
                </Link>
              )}
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-gold-dark text-base uppercase tracking-[0.3em] mb-1 block">
                {localizedSubtitle || t('home.completeExperience')}
              </span>
              <h2 className="font-display text-4xl md:text-5xl text-foreground mb-6">
                {localizedTitle || t('home.signatureBox')}
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {localizedDescription}
              </p>
              {description2 && (
                <p className="text-muted-foreground leading-relaxed">
                  {description2}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Corporate Gifting - Compact Banner Left
  if (section_key === 'corporate_gifting' || layoutType === 'compact_left') {
    return (
      <section className="pt-3 pb-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-row items-center gap-6">
              {imageUrl && (
                <div className="w-32 md:w-40 lg:w-48 flex-shrink-0 overflow-hidden rounded-xl bg-background">
                  <img
                    src={imageUrl}
                    alt={localizedTitle}
                    className="w-full h-auto object-cover shadow-elevated"
                    loading="lazy"
                    width={192}
                    height={288}
                  />
                </div>
              )}
              <div className="flex-1">
                <span className="text-gold-dark text-sm md:text-base uppercase tracking-[0.2em] md:tracking-[0.3em] mb-1 block">
                  {localizedSubtitle || t('home.corporateExcellence')}
                </span>
                <h2 className="font-display text-xl md:text-3xl lg:text-4xl text-foreground mb-2 md:mb-4">
                  {localizedTitle}
                </h2>
                <p className="text-muted-foreground text-sm md:text-base mb-3 md:mb-4 leading-relaxed">
                  {localizedDescription}
                </p>
                <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                  {ctaLink && (
                    <a
                      href={ctaLink.startsWith('http') ? ctaLink : formatWhatsAppLink()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="gold" size="sm" className="gap-2 md:text-base md:px-6 md:py-5">
                        {ctaText || t('btn.contactUs')}
                        <ArrowRight className={arrowClass} />
                      </Button>
                    </a>
                  )}
                  {learnMoreLink && (
                    <Link to={learnMoreLink}>
                      <Button variant="outline" size="sm" className="gap-2 border-gold/30 text-foreground hover:bg-gold/10 md:text-base md:px-6 md:py-5">
                        {t('btn.learnMore')}
                        <ArrowRight className={arrowClass} />
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Special Occasions - Compact Banner Right
  if (section_key === 'special_occasions' || layoutType === 'compact_right') {
    return (
      <section className="pt-3 pb-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-row-reverse items-center gap-6">
              {imageUrl && (
                <div className="w-32 md:w-40 lg:w-48 flex-shrink-0 overflow-hidden rounded-xl bg-background">
                  <img
                    src={imageUrl}
                    alt={localizedTitle}
                    className="w-full h-auto object-cover shadow-elevated"
                    loading="lazy"
                    width={192}
                    height={192}
                  />
                </div>
              )}
              <div className="flex-1">
                <span className="text-gold-dark text-sm md:text-base uppercase tracking-[0.2em] md:tracking-[0.3em] mb-1 block">
                  {localizedSubtitle}
                </span>
                <h2 className="font-display text-xl md:text-3xl lg:text-4xl text-foreground mb-2 md:mb-4">
                  {localizedTitle}
                </h2>
                <p className="text-muted-foreground text-sm md:text-base mb-3 md:mb-4 leading-relaxed">
                  {localizedDescription}
                </p>
                <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                  {ctaLink && (
                    <a
                      href={ctaLink.startsWith('http') ? ctaLink : formatWhatsAppLink()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="gold" size="sm" className="gap-2 md:text-base md:px-6 md:py-5">
                        {ctaText || t('btn.contactUs')}
                        <ArrowRight className={arrowClass} />
                      </Button>
                    </a>
                  )}
                  {learnMoreLink && (
                    <Link to={learnMoreLink}>
                      <Button variant="outline" size="sm" className="gap-2 border-gold/30 text-foreground hover:bg-gold/10 md:text-base md:px-6 md:py-5">
                        {t('btn.learnMore')}
                        <ArrowRight className={arrowClass} />
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Final Expression - Full Width Background
  if (section_key === 'final_expression' || layoutType === 'fullwidth_bg') {
    return (
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          {imageUrl && (
            <img
              src={imageUrl}
              alt={localizedTitle}
              className="w-full h-full object-cover object-top animate-gentle-present lg:object-[85%_25%]"
              loading="lazy"
            />
          )}
          <div className="absolute inset-0 bg-background/80" />
        </div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <h2 className="font-display text-4xl md:text-6xl text-foreground mb-6">
            {localizedTitle}
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            {localizedDescription}
          </p>
          {ctaLink && (
            <Link to={ctaLink} onClick={() => ctaLink === "/quiz" && sessionStorage.setItem("startFreshQuiz", "true")}>
              <Button variant="gold" size="xl" className="gap-2">
                {ctaText || t('home.beginYourJourney')}
                <ArrowRight className={`h-5 w-5${isRTL ? " rotate-180" : ""}`} />
              </Button>
            </Link>
          )}
        </div>
      </section>
    );
  }

  // Anaya Concept Section - Left aligned with CTA and reduced height
  if (section_key === 'anaya_concept') {
    const giftLabel = getLocalizedField(conceptText(content, 'gift_cta_text'), conceptText(content_ar, 'gift_cta_text')) || t('btn.designYourGift');
    const giftLink = conceptGiftDestination(conceptText(content, 'gift_cta_link'));
    const excerpt = getLocalizedField(conceptText(content, 'description_excerpt'), conceptText(content_ar, 'description_excerpt'));
    const conceptButtonClass = 'min-w-0 max-w-full h-full min-h-12 px-2 py-3 text-sm sm:px-5 sm:text-base whitespace-normal break-words [overflow-wrap:anywhere] tracking-normal gap-1 sm:gap-2';
    return (
      <section className="py-8 bg-background" data-section-key="anaya_concept">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="text-start min-w-0">
              {localizedSubtitle && (
                <span className="text-gold-dark text-base uppercase tracking-[0.3em] mb-4 block">
                  {localizedSubtitle}
                </span>
              )}
              <h2 className="font-display text-4xl md:text-5xl text-foreground mb-6">
                {localizedTitle}
              </h2>
              <AnayaConceptDescription key={`${section.id}-${isRTL}`} description={localizedDescription || ''} excerpt={excerpt} />
              <div className="grid grid-cols-2 items-stretch gap-2 sm:gap-4" data-concept-ctas>
                <Button asChild variant="gold" size="lg" className={`${conceptButtonClass} justify-self-start`}>
                  <Link to="/quiz" onClick={() => sessionStorage.setItem("startFreshQuiz", "true")}>
                    <span className="min-w-0">{t('btn.createYourScent')}</span>
                    <ArrowRight className={arrowClass} aria-hidden="true" />
                  </Link>
                </Button>
                {giftLink ? (
                  <Button asChild variant="gold" size="lg" className={`${conceptButtonClass} justify-self-end`}>
                    <Link to={giftLink}>
                      <span className="min-w-0">{giftLabel}</span>
                      <ArrowRight className={arrowClass} aria-hidden="true" />
                    </Link>
                  </Button>
                ) : (
                  <Button disabled variant="gold" size="lg" className={`${conceptButtonClass} justify-self-end`}>
                    <span className="min-w-0">{giftLabel}</span>
                    <ArrowRight className={arrowClass} aria-hidden="true" />
                  </Button>
                )}
              </div>
            </div>
            {imageUrl && (
              <div className="w-full">
                <AspectRatio ratio={3 / 2} className="relative overflow-hidden rounded-2xl bg-muted">
                  <img
                    src={imageUrl}
                    alt={localizedTitle}
                    className="h-full w-full object-cover shadow-elevated brightness-110 saturate-125 contrast-105"
                    loading="lazy"
                  />
                  <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gold/20 rounded-full blur-3xl" />
                </AspectRatio>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Generic/Default Section - Simple centered layout with optional image
  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          {localizedSubtitle && (
            <span className="text-gold-dark text-base uppercase tracking-[0.3em] mb-4 block">
              {localizedSubtitle}
            </span>
          )}
          <h2 className="font-display text-4xl md:text-5xl text-foreground mb-6">
            {localizedTitle}
          </h2>
          {localizedDescription && (
            <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
              {localizedDescription}
            </p>
          )}
          {imageUrl && (
            <div className="relative overflow-hidden rounded-2xl mb-8 max-w-2xl mx-auto">
              <img
                src={imageUrl}
                alt={localizedTitle}
                className="w-full h-auto object-cover shadow-elevated"
              />
            </div>
          )}
          {ctaLink && (
            <Link to={ctaLink} onClick={() => ctaLink === "/quiz" && sessionStorage.setItem("startFreshQuiz", "true")}>
              <Button variant="gold" size="lg" className="gap-2">
                {ctaText || t('btn.learnMore')}
                <ArrowRight className={arrowClass} />
              </Button>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export default HomepageSectionComponent;
